/**
 * Anonymous landing-page instrumentation + campaign attribution.
 *
 * Why this exists: `trackEvent` only dispatches a DOM event / gtag call, and
 * the Data Graph tracker deliberately drops events from signed-out visitors —
 * which is every paid-traffic visitor before signup. This module writes a row
 * to `public.lp_events` (anon INSERT allowed, admin-only SELECT) so the
 * LP view -> CTA click -> signup -> Stack run funnel is measurable from our
 * own data.
 *
 * It also stashes the campaign params on first touch so they can be attached
 * to `user_attributes` at signup, after the visitor has left the LP URL.
 */
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "ha_campaign_params";

export type CampaignParams = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  fbclid: string | null;
};

const EMPTY: CampaignParams = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  fbclid: null,
};

const fromSearch = (search: string): CampaignParams => {
  const p = new URLSearchParams(search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    utm_content: p.get("utm_content"),
    fbclid: p.get("fbclid"),
  };
};

const hasAny = (c: CampaignParams) => Object.values(c).some(Boolean);

/** Capture campaign params on first touch. Later pages keep the original set. */
export function captureCampaignParams(): CampaignParams {
  if (typeof window === "undefined") return EMPTY;
  const current = fromSearch(window.location.search);
  try {
    if (hasAny(current)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      return current;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...EMPTY, ...JSON.parse(stored) };
  } catch {
    /* storage unavailable — fall through to whatever the URL gave us */
  }
  return current;
}

/** Read the stored campaign params (used at signup to attribute the member). */
export function readCampaignParams(): CampaignParams {
  if (typeof window === "undefined") return EMPTY;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return { ...EMPTY, ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return fromSearch(window.location.search);
}

/** Log an anonymous landing-page event. Never throws. */
export function logLpEvent(eventName: string, meta: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const campaign = captureCampaignParams();
  void supabase
    .from("lp_events")
    .insert({
      event_name: eventName,
      path: window.location.pathname,
      referrer: document.referrer || null,
      meta: meta as never,
      ...campaign,
    })
    .then(
      () => {},
      () => {},
    );
}

/**
 * Log an anonymous event at most once per browser session.
 * Used for Homie surfaces (page view, chat open) that can mount repeatedly
 * within one visit and would otherwise inflate the counts.
 */
export function logLpEventOnce(dedupeKey: string, eventName: string, meta: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  try {
    const k = `ha_lp_once:${dedupeKey}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
  } catch {
    /* storage unavailable — log anyway */
  }
  logLpEvent(eventName, meta);
}
